import { Link } from "react-router-dom";
import { Clock, User } from "lucide-react";
import { useLang, localizeArticle } from "@/context/LanguageContext";

const AGO = {
  it: { min: "min fa", hours: "ore fa", days: "giorni fa" },
  en: { min: "min ago", hours: "hours ago", days: "days ago" },
};

export function timeAgo(iso, lang = "it") {
  const l = AGO[lang] || AGO.it;
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} ${l.min}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ${l.hours}`;
  const d = Math.floor(diff / 86400);
  if (d < 30) return `${d} ${l.days}`;
  return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export function HeroCard({ article: raw, testid = "hero-article" }) {
  const { lang } = useLang();
  const article = localizeArticle(raw, lang);
  if (!article) return null;
  return (
    <Link to={`/articolo/${article.slug}`} data-testid={testid} className="group block rounded-sm overflow-hidden bg-legend-ink">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={article.cover_image} alt={article.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-legend-ink to-transparent" />
        <span className="absolute top-4 left-4 bg-legend-red text-white text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-sm">{article.category}</span>
      </div>
      <div className="relative px-6 pb-6 pt-2 md:px-8 md:pb-8 text-white">
        <h1 className="font-display font-black uppercase text-2xl md:text-4xl leading-[0.95] mb-3 max-w-3xl group-hover:text-legend-yellow transition-colors">{article.title}</h1>
        <p className="text-slate-200 max-w-2xl line-clamp-2 mb-4">{article.excerpt}</p>
        <div className="flex items-center gap-4 text-xs font-mono text-slate-300">
          <span className="flex items-center gap-1"><User size={13} /> {article.author}</span>
          <span className="flex items-center gap-1"><Clock size={13} /> {article.read_time} min · {timeAgo(article.published_at, lang)}</span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCard({ article: raw, compact = false }) {
  const { lang } = useLang();
  const article = localizeArticle(raw, lang);
  if (compact) {
    return (
      <Link to={`/articolo/${article.slug}`} data-testid={`article-card-${article.slug}`} className="group flex gap-4 items-start p-3 rounded-sm hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
        <img src={article.cover_image} alt={article.title} className="w-24 h-20 object-cover rounded-sm shrink-0" />
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-legend-red">{article.category}</span>
          <h3 className="font-display font-bold uppercase leading-tight text-slate-900 dark:text-white group-hover:text-legend-red transition-colors line-clamp-2">{article.title}</h3>
          <p className="text-[11px] text-slate-400 font-mono mt-1">{timeAgo(article.published_at, lang)}</p>
        </div>
      </Link>
    );
  }
  return (
    <Link to={`/articolo/${article.slug}`} data-testid={`article-card-${article.slug}`} className="group block rounded-sm overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper hover:shadow-lg hover:-translate-y-1 transition-all">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-legend-ink/90 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm">{article.category}</span>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold uppercase text-lg leading-tight text-slate-900 dark:text-white group-hover:text-legend-red transition-colors line-clamp-2">{article.title}</h3>
        <p className="text-sm text-slate-500 line-clamp-2 mt-2">{article.excerpt}</p>
        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400 mt-3">
          <span className="flex items-center gap-1"><User size={11} /> {article.author}</span>
          <span className="flex items-center gap-1"><Clock size={11} /> {timeAgo(article.published_at, lang)}</span>
        </div>
      </div>
    </Link>
  );
}
